// UTM attribution helpers with first-touch persistence in localStorage.
// First-touch wins: the first UTM set a visitor arrives with is preserved
// for 90 days, even if they later return via direct/organic before converting.

const STORAGE_KEY = "xp_first_touch_utms";
const LAST_TOUCH_KEY = "xp_last_touch_utms";
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

type UtmKey = (typeof UTM_KEYS)[number];
export type Utms = Partial<Record<UtmKey, string>>;

interface StoredUtms {
  utms: Utms;
  first_touch_at: string; // ISO
}

const safeGetItem = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // private mode / quota — silently ignore
  }
};

const readUtmsFromUrl = (): Utms => {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const out: Utms = {};
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) out[k] = v;
  }
  return out;
};

const hasAnyUtm = (u: Utms): boolean => UTM_KEYS.some((k) => !!u[k]);

const readStored = (key: string): StoredUtms | null => {
  const raw = safeGetItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredUtms;
    if (!parsed?.first_touch_at) return null;
    const age = Date.now() - new Date(parsed.first_touch_at).getTime();
    if (Number.isNaN(age) || age > TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Capture UTMs on app load. Call once at startup.
 * - Stores first-touch on the very first UTM-bearing visit (never overwrites).
 * - Always updates last-touch when URL has UTMs.
 */
export const captureUtmsOnLoad = (): void => {
  if (typeof window === "undefined") return;
  const urlUtms = readUtmsFromUrl();
  if (!hasAnyUtm(urlUtms)) return;

  const nowIso = new Date().toISOString();

  // First-touch: only write if nothing valid is stored
  const existing = readStored(STORAGE_KEY);
  if (!existing) {
    const payload: StoredUtms = { utms: urlUtms, first_touch_at: nowIso };
    safeSetItem(STORAGE_KEY, JSON.stringify(payload));
  }

  // Last-touch: always update
  safeSetItem(
    LAST_TOUCH_KEY,
    JSON.stringify({ utms: urlUtms, first_touch_at: nowIso }),
  );
};

export type AttributionType = "url" | "first_touch" | "none";

export interface AttributionResult extends Utms {
  utm_attribution_type: AttributionType;
  utm_first_touch_at?: string;
}

/**
 * Resolve UTMs for analytics/webhook payloads.
 * Priority: current URL → stored first-touch → none.
 */
export const getAttributionUtms = (): AttributionResult => {
  if (typeof window === "undefined") {
    return { utm_attribution_type: "none" };
  }

  const urlUtms = readUtmsFromUrl();
  if (hasAnyUtm(urlUtms)) {
    const firstTouch = readStored(STORAGE_KEY);
    return {
      ...urlUtms,
      utm_attribution_type: "url",
      utm_first_touch_at: firstTouch?.first_touch_at,
    };
  }

  const stored = readStored(STORAGE_KEY);
  if (stored && hasAnyUtm(stored.utms)) {
    return {
      ...stored.utms,
      utm_attribution_type: "first_touch",
      utm_first_touch_at: stored.first_touch_at,
    };
  }

  return { utm_attribution_type: "none" };
};
